/* ==========================================================================
   Eastmonts 2026 — interaction layer
   --------------------------------------------------------------------------
   Every behaviour is a custom element. That gives us Theme Editor safety for
   free: `connectedCallback` fires whenever Shopify injects re-rendered section
   markup, and `disconnectedCallback` tears listeners down again, so nothing
   leaks and nothing needs a `shopify:section:load` hook.

   Rules followed throughout:
   - No global state. All state lives on the element instance.
   - No document-wide selectors. Everything is queried within `this`.
   - No commerce logic. Prices, variants, availability and cart all stay with
     Shopify / the theme's own components.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 900px)';
  var reduceMotion = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /** Register only once — the script may be re-evaluated in the editor. */
  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }

  /* ------------------------------------------------------------------------
     <em-accordion> — accessible disclosure group.
     data-single="true" closes siblings when one opens.
     ------------------------------------------------------------------------ */
  class EmAccordion extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    get single() {
      return this.dataset.single === 'true';
    }

    _onClick(event) {
      var trigger = event.target.closest('.em-acc__trigger');
      if (!trigger || !this.contains(trigger)) return;
      this.toggle(trigger, trigger.getAttribute('aria-expanded') !== 'true');
    }

    toggle(trigger, open) {
      var panel = this.querySelector('#' + CSS.escape(trigger.getAttribute('aria-controls')));
      if (this.single && open) {
        this.querySelectorAll('.em-acc__trigger[aria-expanded="true"]').forEach(
          function (other) {
            if (other !== trigger) this.toggle(other, false);
          }.bind(this)
        );
      }
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) {
        panel.dataset.open = open ? 'true' : 'false';
        panel.hidden = false;
      }
      this.dispatchEvent(
        new CustomEvent('em:accordion:toggle', {
          bubbles: true,
          detail: { trigger: trigger, open: open, index: Number(trigger.dataset.index || 0) }
        })
      );
    }

    openIndex(index) {
      var trigger = this.querySelector('.em-acc__trigger[data-index="' + index + '"]');
      if (trigger) this.toggle(trigger, true);
    }
  }
  define('em-accordion', EmAccordion);

  /* ------------------------------------------------------------------------
     <em-ship-date> — "ships by" date, today plus data-days.

     Was an inline DOMContentLoaded script inside a custom_liquid block, which
     never re-ran when the Theme Editor re-rendered the section, leaving the
     date blank. As an element it fills itself in whenever it is connected.
     ------------------------------------------------------------------------ */
  class EmShipDate extends HTMLElement {
    connectedCallback() {
      var days = Number(this.dataset.days || 0);
      var date = new Date();
      date.setDate(date.getDate() + days);
      this.textContent = date.toLocaleDateString(document.documentElement.lang || 'en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  }
  define('em-ship-date', EmShipDate);

  /* ------------------------------------------------------------------------
     <em-run> — nothing but a custom element.

     The scene is driven entirely by CSS scroll-driven animation and the
     @supports fallback composes a finished still, so there is no behaviour to
     attach and no observer to run. The element exists so the section has a
     tag of its own and so this file documents that the omission is deliberate
     rather than forgotten.
     ------------------------------------------------------------------------ */
  define('em-run', class extends HTMLElement {});

  /* ------------------------------------------------------------------------
     <em-hotspots> — "A closer look": image pins bound to the callout list,
     with the design's 12s auto-advance.
     ------------------------------------------------------------------------ */
  class EmHotspots extends HTMLElement {
    connectedCallback() {
      this.accordion = this.querySelector('em-accordion');
      this.pins = Array.prototype.slice.call(this.querySelectorAll('.em-closer__pin'));
      this.delay = Number(this.dataset.autoplay || 0) * 1000;

      this._onPin = this._onPin.bind(this);
      this._onToggle = this._onToggle.bind(this);
      this.addEventListener('click', this._onPin);
      this.addEventListener('em:accordion:toggle', this._onToggle);

      // Reflect whichever row Liquid rendered as open.
      var open = this.querySelector('.em-acc__trigger[aria-expanded="true"]');
      this._sync(open ? Number(open.dataset.index) : -1);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onPin);
      this.removeEventListener('em:accordion:toggle', this._onToggle);
      clearTimeout(this._timer);
    }

    _onPin(event) {
      var pin = event.target.closest('.em-closer__pin');
      if (!pin || !this.contains(pin)) return;
      var index = Number(pin.dataset.index);
      var isActive = pin.getAttribute('aria-pressed') === 'true';
      if (!this.accordion || typeof this.accordion.toggle !== 'function') return;
      var trigger = this.accordion.querySelector('.em-acc__trigger[data-index="' + index + '"]');
      if (trigger) this.accordion.toggle(trigger, !isActive);
    }

    _onToggle(event) {
      this._sync(event.detail.open ? event.detail.index : -1);
    }

    _sync(index) {
      this.current = index;
      this.pins.forEach(function (pin) {
        var on = Number(pin.dataset.index) === index;
        pin.setAttribute('aria-pressed', on ? 'true' : 'false');
        pin.classList.toggle('is-active', on);
      });

      // Restart the fill animation on the newly opened row.
      this.querySelectorAll('.em-closer__progress').forEach(function (bar) {
        bar.classList.remove('is-running');
      });
      clearTimeout(this._timer);
      if (index < 0 || !this.delay || reduceMotion()) return;

      var active = this.querySelector('.em-closer__row[data-index="' + index + '"] .em-closer__progress');
      if (active) {
        void active.offsetWidth; // force reflow so the animation replays
        active.style.setProperty('--em-fill-duration', this.delay + 'ms');
        active.classList.add('is-running');
      }
      this._timer = setTimeout(
        function () {
          if (!this.isConnected || !this.accordion) return;
          if (typeof this.accordion.openIndex !== 'function') return;
          var next = (index + 1) % Math.max(this.pins.length || 1, 1);
          this.accordion.openIndex(next);
        }.bind(this),
        this.delay
      );
    }
  }
  define('em-hotspots', EmHotspots);

  /* ------------------------------------------------------------------------
     <em-video-card> — poster -> real <video>, created only on demand so no
     video bytes are fetched until the visitor asks for them.
     ------------------------------------------------------------------------ */
  class EmVideoCard extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.stop();
    }

    get playing() {
      return this.classList.contains('is-playing');
    }

    _onClick(event) {
      if (!event.target.closest('.em-video-card__play')) return;
      // Ignore the click that ends a drag on the surrounding rail.
      if (this.closest('[data-dragged="true"]')) return;
      this.play();
    }

    play() {
      if (this.playing) return;
      var stage = this.querySelector('.em-video-card__stage');
      var template = this.querySelector('.em-video-card__template');
      var embed = this.dataset.embedSrc;
      if (!stage || (!template && !embed)) return;

      // Only one story plays at a time within a rail.
      var rail = this.closest('em-story-rail');
      if (rail) rail.stopAll(this);

      // External (YouTube/Vimeo) videos are embedded rather than played inline.
      if (!template && embed) {
        var frame = document.createElement('iframe');
        frame.className = 'em-video-card__video';
        frame.src = embed + (embed.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1';
        frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('title', this.dataset.videoTitle || 'Customer story');
        stage.appendChild(frame);
        this.classList.add('is-playing');
        this._video = frame;
        this.dispatchEvent(new CustomEvent('em:video:play', { bubbles: true }));
        return;
      }

      var fragment = template.content.cloneNode(true);
      var video = fragment.querySelector('video');
      if (!video) return;
      video.classList.add('em-video-card__video');
      hlsAan(video);
      video.setAttribute('preload', 'auto');
      video.addEventListener('pause', this.stop.bind(this));
      stage.appendChild(fragment);
      this.classList.add('is-playing');
      this._video = video;
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
      this.dispatchEvent(new CustomEvent('em:video:play', { bubbles: true }));
    }

    stop() {
      if (!this._video) return;
      if (this._video.pause) this._video.pause();
      this._video.remove();
      this._video = null;
      this.classList.remove('is-playing');
      this.dispatchEvent(new CustomEvent('em:video:stop', { bubbles: true }));
    }
  }
  define('em-video-card', EmVideoCard);

  /* ------------------------------------------------------------------------
     <em-story-rail> — "Real riders". Desktop: seamless auto-marquee that you
     can grab and drag. Mobile: scroll-snap carousel with prev/next.
     The track is cloned from the Liquid-rendered cards, so adding or removing
     a block in the Theme Editor needs no JS change.
     ------------------------------------------------------------------------ */
  class EmStoryRail extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.em-stories__viewport');
      this.track = this.querySelector('.em-stories__track');
      if (!this.viewport || !this.track) return;

      this.originals = Array.prototype.slice.call(this.track.children);
      this.speed = Number(this.dataset.speed || 0.7);
      this._paused = false;
      this._playing = false;

      this._mq = window.matchMedia(DESKTOP);
      this._onChange = this._setup.bind(this);
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._onChange);
      else this._mq.addListener(this._onChange);

      this._bind();
      this._setup();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._raf);
      clearTimeout(this._wrapT);
      if (this._onScrollWrap) this.viewport.removeEventListener('scroll', this._onScrollWrap);
      if (this._mq) {
        if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onChange);
        else this._mq.removeListener(this._onChange);
      }
      this._unbind();
    }

    _bind() {
      this._handlers = {
        enter: function () { this._paused = true; }.bind(this),
        leave: function () { this._paused = false; this._endDrag(); }.bind(this),
        down: this._startDrag.bind(this),
        move: this._drag.bind(this),
        up: this._endDrag.bind(this),
        play: function () { this._playing = true; }.bind(this),
        stop: function () { this._playing = false; }.bind(this)
      };
      this.viewport.addEventListener('mouseenter', this._handlers.enter);
      this.viewport.addEventListener('mouseleave', this._handlers.leave);
      this.viewport.addEventListener('pointerdown', this._handlers.down);
      this.viewport.addEventListener('pointermove', this._handlers.move);
      this.viewport.addEventListener('pointerup', this._handlers.up);
      this.viewport.addEventListener('pointercancel', this._handlers.up);
      this.addEventListener('em:video:play', this._handlers.play);
      this.addEventListener('em:video:stop', this._handlers.stop);

      this._onNav = this._onNav.bind(this);
      this.addEventListener('click', this._onNav);
    }

    _unbind() {
      if (!this._handlers) return;
      this.viewport.removeEventListener('mouseenter', this._handlers.enter);
      this.viewport.removeEventListener('mouseleave', this._handlers.leave);
      this.viewport.removeEventListener('pointerdown', this._handlers.down);
      this.viewport.removeEventListener('pointermove', this._handlers.move);
      this.viewport.removeEventListener('pointerup', this._handlers.up);
      this.viewport.removeEventListener('pointercancel', this._handlers.up);
      this.removeEventListener('em:video:play', this._handlers.play);
      this.removeEventListener('em:video:stop', this._handlers.stop);
      this.removeEventListener('click', this._onNav);
    }

    /** Rebuild the track for the active breakpoint. */
    _setup() {
      cancelAnimationFrame(this._raf);
      clearTimeout(this._wrapT);
      if (this._onScrollWrap) this.viewport.removeEventListener('scroll', this._onScrollWrap);
      this.stopAll();
      this.track.querySelectorAll('[data-em-clone]').forEach(function (n) { n.remove(); });

      // The design loops at both breakpoints: desktop drifts on its own,
      // mobile wraps as you swipe so the carousel never hits an end.
      var loop = this.originals.length > 1 && !reduceMotion();
      this.classList.toggle('is-looping', loop);

      if (loop) {
        // Two passes are enough for a seamless wrap at any viewport width.
        for (var pass = 0; pass < 2; pass++) {
          this.originals.forEach(function (card) {
            var clone = card.cloneNode(true);
            clone.setAttribute('data-em-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            // Clones must never be focusable or addressable by the editor.
            clone.removeAttribute('id');
            Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (n) {
              n.removeAttribute('id');
            });
            Array.prototype.forEach.call(clone.querySelectorAll('button, a, video'), function (n) {
              n.setAttribute('tabindex', '-1');
            });
            this.track.appendChild(clone);
          }, this);
        }
        if (this._mq.matches) this._loop();
        else this._wrapOnScroll();
      }
    }

    /** Mobile: keep the swipe endless by shifting a whole set when we near an
        edge, so there is always a set of cards either side of the visitor. */
    _wrapOnScroll() {
      var setWidth = function () { return this.track.scrollWidth / 3; }.bind(this);

      var centre = function () {
        var w = setWidth();
        if (w) this.viewport.scrollLeft = w;
        else requestAnimationFrame(centre);
      }.bind(this);
      requestAnimationFrame(centre);

      this._onScrollWrap = function () {
        clearTimeout(this._wrapT);
        this._wrapT = setTimeout(
          function () {
            var w = setWidth();
            if (!w || !this.isConnected) return;
            var prev = this.viewport.style.scrollBehavior;
            this.viewport.style.scrollBehavior = 'auto';
            if (this.viewport.scrollLeft < w * 0.5) this.viewport.scrollLeft += w;
            else if (this.viewport.scrollLeft > w * 1.5) this.viewport.scrollLeft -= w;
            this.viewport.style.scrollBehavior = prev;
          }.bind(this),
          140
        );
      }.bind(this);

      this.viewport.addEventListener('scroll', this._onScrollWrap, { passive: true });
    }

    _loop() {
      var step = function () {
        if (!this.isConnected) return;
        var half = this.track.scrollWidth / 3; // one original pass
        if (half > 0) {
          if (!this._paused && !this._dragging && !this._playing) {
            this.viewport.scrollLeft += this.speed;
          }
          if (this.viewport.scrollLeft >= half * 2) this.viewport.scrollLeft -= half;
          else if (this.viewport.scrollLeft <= 0) this.viewport.scrollLeft += half;
        }
        this._raf = requestAnimationFrame(step);
      }.bind(this);
      this._raf = requestAnimationFrame(step);
    }

    _startDrag(event) {
      if (event.target.closest('video')) return;
      this._dragging = true;
      this._moved = 0;
      this._x = event.clientX;
      this._start = this.viewport.scrollLeft;
      this.viewport.classList.add('is-grabbing');
    }

    _drag(event) {
      if (!this._dragging) return;
      var delta = event.clientX - this._x;
      this._moved = Math.max(this._moved, Math.abs(delta));
      this.viewport.scrollLeft = this._start - delta;
      if (this._moved > 6) this.dataset.dragged = 'true';
    }

    _endDrag() {
      if (!this._dragging) return;
      this._dragging = false;
      this.viewport.classList.remove('is-grabbing');
      // Let the click that ends a drag pass before re-enabling play taps.
      setTimeout(function () { delete this.dataset.dragged; }.bind(this), 0);
    }

    _onNav(event) {
      var button = event.target.closest('[data-em-nav]');
      if (!button || !this.contains(button)) return;
      var dir = button.dataset.emNav === 'next' ? 1 : -1;
      var cards = Array.prototype.slice.call(this.track.children);
      if (!cards.length) return;
      var mid = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      var current = 0;
      var best = Infinity;
      cards.forEach(function (card, i) {
        var d = Math.abs(card.offsetLeft + card.offsetWidth / 2 - mid);
        if (d < best) { best = d; current = i; }
      });
      var target = cards[Math.max(0, Math.min(cards.length - 1, current + dir))];
      if (target) {
        this.viewport.scrollTo({
          left: target.offsetLeft + target.offsetWidth / 2 - this.viewport.clientWidth / 2,
          behavior: 'smooth'
        });
      }
    }

    stopAll(except) {
      this.querySelectorAll('em-video-card').forEach(function (card) {
        // Child custom elements are not guaranteed to be upgraded yet when the
        // rail connects (Theme Editor re-render), so `stop` may not exist.
        if (card !== except && typeof card.stop === 'function') card.stop();
      });
    }
  }
  define('em-story-rail', EmStoryRail);

  /* ------------------------------------------------------------------------
     <em-clamp> — Read more / Read less.
     Truncates at a word boundary only when the text actually exceeds the
     threshold, so short reviews never render a control.
     ------------------------------------------------------------------------ */
  class EmClamp extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('.em-clamp__body');
      this.button = this.querySelector('.em-clamp__toggle');
      if (!this.body || !this.button) return;

      this.limit = Number(this.dataset.limit || 260);
      this.full = (this.dataset.full || this.body.textContent || '').trim();
      this.moreLabel = this.dataset.more || 'Read more';
      this.lessLabel = this.dataset.less || 'Read less';

      if (this.full.length <= this.limit) {
        this.button.hidden = true;
        this.body.textContent = this.full;
        return;
      }

      var cut = this.full.slice(0, this.limit);
      var space = cut.lastIndexOf(' ');
      this.short =
        (space > this.limit - 40 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?-]+$/, '') + '…';

      this.button.hidden = false;
      this._onClick = this._onClick.bind(this);
      this.button.addEventListener('click', this._onClick);
      this._render(false);
    }

    disconnectedCallback() {
      if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
    }

    _onClick(event) {
      event.stopPropagation();
      this._render(this.button.getAttribute('aria-expanded') !== 'true');
    }

    _render(expanded) {
      this.body.textContent = expanded ? this.full : this.short;
      this.button.textContent = expanded ? this.lessLabel : this.moreLabel;
      this.button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }
  define('em-clamp', EmClamp);

  /* ------------------------------------------------------------------------
     <em-review-wall> — desktop: three parallax columns built from the flat
     Liquid card list. Mobile: the same flat list as a snap carousel.
     ------------------------------------------------------------------------ */
  class EmReviewWall extends HTMLElement {
    connectedCallback() {
      this.flat = this.querySelector('.em-reviews__flat');
      this.columns = this.querySelector('.em-reviews__columns');
      if (!this.flat || !this.columns) return;

      this.cards = Array.prototype.slice.call(this.flat.children);
      var raw = parseFloat(this.dataset.parallax);
      this.intensity = isNaN(raw) ? 1 : raw;
      this._mq = window.matchMedia(DESKTOP);
      this._onChange = this._setup.bind(this);
      if (this._mq.addEventListener) this._mq.addEventListener('change', this._onChange);
      else this._mq.addListener(this._onChange);

      this._offset = 0;
      this._onScroll = this._paint.bind(this);
      this._onDown = this._down.bind(this);
      this._onMove = this._move.bind(this);
      this._onUp = this._up.bind(this);
      this._onNav = this._nav.bind(this);
      this.addEventListener('click', this._onNav);

      this._setup();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      this.columns.removeEventListener('pointerdown', this._onDown);
      this.columns.removeEventListener('pointermove', this._onMove);
      this.columns.removeEventListener('pointerup', this._onUp);
      this.columns.removeEventListener('pointercancel', this._onUp);
      this.removeEventListener('click', this._onNav);
      if (this.flat) this.flat.removeEventListener('scroll', this._onTrack);
    }

    _setup() {
      var desktop = this._mq.matches && !reduceMotion();
      this.classList.toggle('is-desktop', desktop);

      // Always restore the flat list first so the two modes never fight.
      this.cards.forEach(function (card) { this.flat.appendChild(card); }, this);
      this.columns.innerHTML = '';
      window.removeEventListener('scroll', this._onScroll);
      this.columns.removeEventListener('pointerdown', this._onDown);
      this.columns.removeEventListener('pointermove', this._onMove);
      this.columns.removeEventListener('pointerup', this._onUp);
      this.columns.removeEventListener('pointercancel', this._onUp);

      if (!desktop) {
        this._onTrack = this._track.bind(this);
        this.flat.addEventListener('scroll', this._onTrack, { passive: true });
        this._track();
        return;
      }

      // Greedy balance by estimated card cost, mirroring the design.
      var buckets = [[], [], []];
      var cost = [0, 0, 0];
      this.cards.forEach(function (card) {
        var i = cost.indexOf(Math.min.apply(null, cost));
        buckets[i].push(card);
        cost[i] += Math.min(card.textContent.trim().length, 420) + 160;
      });

      this._cols = buckets.map(function (bucket) {
        var col = document.createElement('div');
        col.className = 'em-reviews__col';
        bucket.forEach(function (card) { col.appendChild(card); });
        // Duplicate for a seamless vertical loop; clones are inert.
        bucket.forEach(function (card) {
          var clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('id');
          Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (n) {
            n.removeAttribute('id');
          });
          Array.prototype.forEach.call(clone.querySelectorAll('button, a'), function (n) {
            n.setAttribute('tabindex', '-1');
          });
          col.appendChild(clone);
        });
        this.columns.appendChild(col);
        return col;
      }, this);

      window.addEventListener('scroll', this._onScroll, { passive: true });
      this.columns.addEventListener('pointerdown', this._onDown);
      this.columns.addEventListener('pointermove', this._onMove);
      this.columns.addEventListener('pointerup', this._onUp);
      this.columns.addEventListener('pointercancel', this._onUp);
      this._paint();
    }

    _paint() {
      if (!this._cols) return;
      // Design speeds, scaled by the section's parallax setting (1 = as drawn).
      var k = this.intensity;
      var speeds = [0.16 * k, -0.24 * k, 0.2 * k];
      var base = [0, 0.5, 0.25];
      this._cols.forEach(function (col, i) {
        var half = col.scrollHeight / 2;
        if (!half) return;
        var y = (base[i] * half + (window.scrollY + this._offset) * speeds[i]) % half;
        if (y > 0) y -= half;
        col.style.transform = 'translate3d(0,' + y + 'px,0)';
      }, this);
    }

    _down(event) {
      this._dragging = true;
      this._y = event.clientY;
      this.columns.classList.add('is-grabbing');
    }

    _move(event) {
      if (!this._dragging) return;
      this._offset -= (event.clientY - this._y) * 5;
      this._y = event.clientY;
      this._paint();
    }

    _up() {
      this._dragging = false;
      this.columns.classList.remove('is-grabbing');
    }

    /** Mobile: keep the progress indicator in step with the scroll position. */
    _track() {
      var bar = this.querySelector('.em-reviews__bar-fill');
      if (!bar || !this.cards.length) return;
      var max = this.flat.scrollWidth - this.flat.clientWidth;
      var ratio = max > 0 ? this.flat.scrollLeft / max : 0;
      var width = 100 / this.cards.length;
      bar.style.width = width + '%';
      bar.style.left = ratio * (100 - width) + '%';
    }

    _nav(event) {
      var button = event.target.closest('[data-em-nav]');
      if (!button || !this.contains(button) || this.classList.contains('is-desktop')) return;
      var dir = button.dataset.emNav === 'next' ? 1 : -1;
      var first = this.cards[0];
      if (!first) return;
      var step = first.getBoundingClientRect().width + 12;
      this.flat.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
  }
  define('em-review-wall', EmReviewWall);

  /* ------------------------------------------------------------------------
     Footer menus — collapsible on mobile, plain lists on desktop.

     The theme renders footer link lists as a heading plus a <ul> with no
     disclosure, so this enhances the existing markup rather than changing the
     Liquid: the heading becomes a real <button> with aria-expanded/-controls
     and the list moves into the shared .em-acc__panel. On desktop the panel is
     forced open and the control is inert, so nothing is hidden from anyone who
     never sees the mobile layout.
     ------------------------------------------------------------------------ */
  var footerMq = window.matchMedia(DESKTOP);

  function enhanceFooterMenu(block) {
    if (block.dataset.emMenu) return;
    var heading = block.querySelector('.footer-block__heading');
    var list = block.querySelector('.footer-block__details-content');
    if (!heading || !list) return;

    // Derive a stable id from the theme's own per-block class.
    var idMatch = /footer-block__details-content-([\w-]+)/.exec(list.className);
    var panelId = 'EmFooterMenu-' + (idMatch ? idMatch[1] : block.dataset.index || 'menu');

    block.dataset.emMenu = 'ready';

    var label = heading.textContent.trim();
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'footer-block__toggle';
    button.setAttribute('aria-controls', panelId);
    button.appendChild(document.createTextNode(label));
    var icon = document.createElement('span');
    icon.className = 'em-acc__icon';
    icon.setAttribute('aria-hidden', 'true');
    button.appendChild(icon);
    heading.textContent = '';
    heading.appendChild(button);

    var panel = document.createElement('div');
    panel.className = 'em-acc__panel footer-block__panel';
    panel.id = panelId;
    var inner = document.createElement('div');
    list.parentNode.insertBefore(panel, list);
    inner.appendChild(list);
    panel.appendChild(inner);

    var sync = function () {
      var desktop = footerMq.matches;
      var open = desktop ? true : button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.dataset.open = open ? 'true' : 'false';
      button.disabled = desktop;
    };

    button.addEventListener('click', function () {
      if (footerMq.matches) return;
      var open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.dataset.open = open ? 'true' : 'false';
    });

    if (footerMq.addEventListener) footerMq.addEventListener('change', sync);
    else footerMq.addListener(sync);
    sync();
  }

  function enhanceFooterMenus(root) {
    (root || document).querySelectorAll('.footer-block--menu').forEach(enhanceFooterMenu);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { enhanceFooterMenus(); });
  } else {
    enhanceFooterMenus();
  }

  /* ------------------------------------------------------------------------
     Sticky Add to cart — mirror the selected bundle.

     The theme's sticky bar renders the plain variant image and variant price,
     which contradicts the buy box whenever a bundle is selected (€59,99 vs
     €29,99). Rather than recompute anything, we copy what the bundle app has
     already rendered in its selected card: its thumbnail, its title and its
     price. No pricing logic lives here — if the app changes the offer, the bar
     follows. The button itself is untouched, so Add to cart stays the theme's
     own product form.
     ------------------------------------------------------------------------ */
  /* The sticky bar and the bundle bars are written by two different owners.
     Kaching redraws the sticky bar when a bundle is selected — with its own
     shorter title, which is why the title is left alone here. The theme writes
     the price whenever the variant changes, and a colour change is a variant
     change: it puts the raw variant price there, EUR 59,99, next to a bundle
     that costs EUR 29,99.

     Nothing corrects that. Proved it by writing rubbish into the bar and
     waiting: it stayed. Only a bundle switch brings anyone back.

     So this does not try to be first — it makes sure it is last. Anything that
     changes, on either side, and the price is put back to whatever the selected
     bundle says. Text included: the price is a text node, and the old version
     watched only `class` and `src`, which is exactly why a colour change slipped
     past it. */
  /* The bundle app prints the whole product as JSON on the page, variants and
     their images included, so the colour can be resolved without asking the
     server for anything. Parsed once and kept. */
  var emVariants = null;

  function variantByOption(value) {
    if (emVariants === null) {
      emVariants = [];
      var node = document.querySelector('script.kaching-bundles-product');
      if (node) {
        try {
          emVariants = (JSON.parse(node.textContent).variants || []);
        } catch (e) {
          emVariants = [];
        }
      }
    }
    var wanted = value.toLowerCase();
    for (var i = 0; i < emVariants.length; i++) {
      var opts = emVariants[i].options || [];
      for (var j = 0; j < opts.length; j++) {
        if (String(opts[j]).toLowerCase() === wanted) return emVariants[i];
      }
    }
    return null;
  }

  var stickySyncing = false;

  function syncStickyBundle() {
    var sticky = document.querySelector('sticky-atc');
    var bar = document.querySelector('.kaching-bundles__bar--selected');
    if (!sticky || !bar || stickySyncing) return;

    stickySyncing = true;

    /* The picture: the selected bundle's own photo, so one unit shows one
       Ski Tunes and the two-pack shows two. Same on every page, V2 and V3
       alike (Quinten, 16 Oct 2026); the V2 bars used to be a "STOREWIDE SALE"
       promo tile, which is why this once read the colour swatch first.

       Falls back to the chosen colour's variant image only when the bar has no
       photo of its own. */
    var thumb = sticky.querySelector('.sticky-atc__image img');
    var newSrc = null;
    var newAlt = null;

    var barImg = bar.querySelector('.kaching-bundles__bar-image');
    if (barImg) {
      newSrc = barImg.getAttribute('src') || barImg.currentSrc;
      newAlt = barImg.getAttribute('alt');
    }

    if (!newSrc) {
      var swatch = bar.querySelector('.kaching-bundles__swatch-dropdown');
      var chosen = swatch && swatch.textContent.replace(/\s+/g, ' ').trim();
      var variant = chosen && variantByOption(chosen);
      if (variant && variant.image) {
        newSrc = variant.image;
        newAlt = chosen;
      }
    }

    if (thumb && newSrc && thumb.getAttribute('src') !== newSrc) {
      thumb.setAttribute('src', newSrc);
      thumb.removeAttribute('srcset');
      if (newAlt) thumb.setAttribute('alt', newAlt);
    }

    /* How many. The bar names its own count — "2× Eastmonts Ski Tunes®" — and
       the sticky bar names none at all, so a two-pack read as a single one next
       to a two-pack price.

       Only the count is taken, not the bar's wording: the sticky bar carries the
       short "Ski Tunes" and the bar the full registered title, and the short one
       is the right one in a strip that thin. So the prefix is rewritten and the
       name left alone — which also makes this safe to run repeatedly, since the
       old prefix is stripped before the new one goes on — every leading count,
       not just one, or two writers in a row leave "1× 2× Ski Tunes". */
    var title = sticky.querySelector('.sticky-atc__title');
    var barTitle = bar.querySelector('.kaching-bundles__bar-title');
    if (title && barTitle) {
      var qty = /^\s*(\d+)\s*[×x]/.exec(barTitle.textContent);
      if (qty) {
        var name = title.textContent.replace(/^(?:\s*\d+\s*[×x]\s*)+/, '').trim();
        var wanted = qty[1] + '× ' + name;
        if (name && title.textContent.trim() !== wanted) title.textContent = wanted;
      }
    }

    var price = sticky.querySelector('.sticky-atc__price');
    var barPrice = bar.querySelector('.kaching-bundles__bar-price');
    if (price && barPrice) {
      var p = barPrice.textContent.replace(/\s+/g, ' ').trim();
      if (p && price.textContent.trim() !== p) price.textContent = p;
    }

    // Released on the next tick so the writes above have been observed and
    // discarded, rather than bouncing straight back in.
    //
    // setTimeout, not requestAnimationFrame: a hidden tab never paints, so rAF
    // never fires there and the guard would latch on forever — one sync while
    // the tab sat in the background and the price would stay wrong for the rest
    // of the visit, including after the visitor came back. Cost me a confusing
    // test to find; a real visitor would have hit it on any backgrounded tab.
    setTimeout(function () {
      stickySyncing = false;
    }, 0);
  }

  function watchStickyBundle() {
    var bars = document.querySelector('.kaching-bundles__bars');
    if (!bars || bars.dataset.emStickySync) return;
    bars.dataset.emStickySync = 'on';
    syncStickyBundle();

    var opts = {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'src']
    };

    new MutationObserver(syncStickyBundle).observe(bars, opts);

    // The sticky bar too, not just the bundles. The wrong price arrives by the
    // theme rewriting the sticky bar itself, and watching only the bundles
    // means never hearing about it.
    var sticky = document.querySelector('sticky-atc');
    if (sticky) new MutationObserver(syncStickyBundle).observe(sticky, opts);
  }

  // The bundle app renders after us, so poll briefly until its markup exists.
  (function waitForBundles(tries) {
    if (document.querySelector('.kaching-bundles__bars')) return watchStickyBundle();
    if (tries > 0) setTimeout(function () { waitForBundles(tries - 1); }, 300);
  })(20);

  /* ----------------------------------------------------------------------
     Deferred video.

     The feature clips carry no `autoplay` attribute, because that attribute
     makes a browser fetch the file as it parses the tag — `preload="none"`
     does not stop it — and these sit far below the fold. They are started
     here instead, as each comes into view, so a visitor downloads a clip when
     they arrive at it rather than three of them while the first screen is
     still painting.

     Paused on the way out too: a looping clip that has scrolled away is
     decoding frames nobody is watching, which costs battery on a phone.

     rootMargin gives it a screen of warning, so playback has usually begun by
     the time the clip is actually in view. If IntersectionObserver is missing,
     everything plays immediately — the old behaviour, not a broken one.
     -------------------------------------------------------------------- */
  /* A device can refuse to start playback and there is no way to ask why —
     iOS Low Power Mode is the common one, and it simply says no. That refusal
     used to be swallowed, so a clip sat frozen on its first frame until it
     happened to scroll out of view and back in. Refused clips are remembered
     here and started on the first touch or scroll, which counts as intent. */
  var wachtOpGebaar = [];

  function speelAf(v) {
    var p = v.play();
    if (p && p.catch) {
      p.catch(function () {
        if (wachtOpGebaar.indexOf(v) === -1) wachtOpGebaar.push(v);
      });
    }
  }

  function naGebaar() {
    wachtOpGebaar.splice(0).forEach(function (v) {
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
  }

  ['touchstart', 'pointerdown', 'scroll'].forEach(function (soort) {
    window.addEventListener(soort, naGebaar, { passive: true });
  });

  /* Shopify transcodes every upload into an adaptive HLS playlist alongside the
     mp4 renditions, and for the long clips on the product page the difference
     is not small: the mp4 of the 52-second rider story is 46 MB, fetched whole,
     while the playlist streams in segments and starts on a rung the connection
     can carry.

     Only WebKit is offered it. Chromium answers canPlayType for HLS with
     "maybe" regardless of whether the build can decode it, so trusting that
     answer risks a browser picking the playlist and then showing a frozen
     poster — the exact failure this page has been bitten by before. Safari's
     support is not a maybe, and it is where most of this store's traffic is.
     Everywhere else the mp4 in the markup is left alone. */
  var kanHls = (function () {
    try {
      var webkit = /Apple/.test(navigator.vendor || '') && !/Chrom(e|ium)|Edg|OPR/.test(navigator.userAgent);
      if (!webkit) return false;
      return !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
    } catch (e) {
      return false;
    }
  })();

  function hlsAan(v) {
    if (!kanHls || !v || !v.dataset || !v.dataset.hls || v.dataset.emHls) return;
    v.dataset.emHls = '1';
    var bron = document.createElement('source');
    bron.src = v.dataset.hls;
    bron.type = 'application/vnd.apple.mpegurl';
    /* In front of the mp4: a browser takes the first source it can play, so an
       mp4 left first would win and the playlist would never be reached. */
    v.insertBefore(bron, v.firstChild);
  }

  function deferVideo(root) {
    var vids = (root || document).querySelectorAll('video.em-video-defer');
    if (!vids.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(vids, function (v) {
        speelAf(v);
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;

          if (!entry.isIntersecting) {
            if (!v.paused) v.pause();
            return;
          }

          // Fetching and playing are asked for separately on purpose. A
          // browser may refuse to start playback — a backgrounded tab, or a
          // device saving power — and if the fetch rode on play() the clip
          // would then still be empty when it is finally allowed to run.
          // Priming preload gets the bytes either way; once only, because
          // load() would otherwise rewind a clip on every pass.
          //
          // Raising preload is the whole of it. This used to also call load()
          // straight after, and the two fought: changing preload starts the
          // fetch, load() then aborts that one and starts it again, so every
          // clip on this page was pulled down twice. Lighthouse showed both
          // requests. Setting the property is enough to resume a source that
          // was parked on preload="none" — load() is kept only for the case
          // where the browser did not take the hint, checked a tick later so
          // it cannot cancel a fetch that is already running.
          if (!v.dataset.emPrimed) {
            v.dataset.emPrimed = '1';
            hlsAan(v);
            v.preload = 'auto';
            setTimeout(function () {
              if (v.networkState !== HTMLMediaElement.NETWORK_LOADING && v.readyState === 0) {
                v.load();
              }
            }, 250);
          }

          speelAf(v);
        });
      },
      { rootMargin: '100% 0px' }
    );

    /* Watching does not start until the page has finished loading.

       rootMargin is a full viewport, so a clip a screen below the fold counts
       as near enough to prime — and on a phone the first feature video is just
       inside that margin, which meant 3.5 MB of video being pulled down while
       the images and scripts of the screen the visitor was actually looking at
       were still arriving. It is the same clip, primed the same way, a moment
       later: waiting for load costs nothing once the page is up, and the margin
       still gets it ready well before it is scrolled to.

       Anyone who scrolls that far before the page has loaded is served by the
       observer firing immediately once it does. */
    var kijken = function () {
      Array.prototype.forEach.call(vids, function (v) {
        io.observe(v);
      });
    };

    if (document.readyState === 'complete') {
      kijken();
    } else {
      window.addEventListener('load', kijken, { once: true });
    }
  }

  deferVideo();

  /* Gallery slides 2 and up arrive with their URLs parked in data-em-src /
     data-em-srcset (see snippets/product-thumbnail.liquid), so they no longer
     compete with the first slide while the page loads. They are handed back
     once it has loaded, or the moment the gallery is touched, whichever is
     first, so a swipe never lands on an empty slide. */
  function slidesVrij() {
    var imgs = document.querySelectorAll('img[data-em-src]');
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.dataset.emSrcset) {
        img.srcset = img.dataset.emSrcset;
        img.removeAttribute('data-em-srcset');
      }
      img.src = img.dataset.emSrc;
      img.removeAttribute('data-em-src');
    });
  }

  /* A variant change swaps the gallery for freshly rendered HTML, with its
     slides parked again. Once the page has loaded nothing should stay parked,
     so anything parked that turns up after that is handed back at once. */
  function slidesBewaken() {
    slidesVrij();
    if (!('MutationObserver' in window)) return;
    // Only added nodes are looked at: the cart timer and the marquees mutate
    // the page all the time, and none of that should cost a document query.
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var n = nodes[j];
          if (n.nodeType !== 1) continue;
          if (n.hasAttribute('data-em-src') || n.querySelector('img[data-em-src]')) {
            slidesVrij();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'complete') {
    slidesBewaken();
  } else {
    window.addEventListener('load', slidesBewaken, { once: true });
  }

  if (document.querySelector('img[data-em-src]')) {
    if (document.readyState !== 'complete') {
      ['pointerdown', 'touchstart', 'focusin'].forEach(function (soort) {
        document.addEventListener(
          soort,
          function (e) {
            if (e.target.closest && e.target.closest('media-gallery, .product__media-wrapper')) slidesVrij();
          },
          { passive: true }
        );
      });
    }
  }

  /* Feature-video posters arrive as data-poster (see snippets/em-video.liquid
     and lazy_poster). A poster is an image the browser fetches on sight of the
     tag, however far down the page, so three of them used to load alongside
     the first screen. Each is set once its video is within half a screen. */
  function postersLaat(root) {
    var vids = (root || document).querySelectorAll('video[data-poster]');
    if (!vids.length) return;

    var zet = function (v) {
      if (!v.dataset.poster) return;
      v.poster = v.dataset.poster;
      v.removeAttribute('data-poster');
    };

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(vids, zet);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          zet(entry.target);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '50% 0px' }
    );

    Array.prototype.forEach.call(vids, function (v) {
      io.observe(v);
    });
  }

  postersLaat();

  // The Theme Editor re-renders the footer section on every edit.
  document.addEventListener('shopify:section:load', function (event) {
    enhanceFooterMenus(event.target);
    syncStickyBundle();
    deferVideo(event.target);
    postersLaat(event.target);
  });
})();

/* --------------------------------------------------------------------------
   How the visitor last interacted: pointer (tap, click) or keyboard.
   When the menu or cart drawer closes, the theme hands focus back to the icon
   that opened it, and some browsers (Safari on a phone above all) then draw
   the keyboard focus ring round it: a dark square that stays after a tap
   (Quinten, 16 Oct 2026). The theme's script is minified and not ours, so this
   only records the input type on <html>; the stylesheet hides the ring on the
   header icons after a tap or click and keeps it for keyboard users. The one
   document-wide listener in this file, on purpose: it is about the whole page.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';
  var root = document.documentElement;
  document.addEventListener('pointerdown', function () {
    root.setAttribute('data-em-input', 'pointer');
  }, true);
  document.addEventListener('keydown', function (event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    root.setAttribute('data-em-input', 'keyboard');
  }, true);
})();
